import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { createProperty, STATES, PROPERTY_TYPES } from "@/lib/api";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";

export default function AddProperty() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    suburb: "",
    state: "",
    postcode: "",
    property_type: "",
    purchase_price: "",
    purchase_date: "",
    current_value: "",
    loan_amount: "",
    interest_rate: "",
    loan_term_years: "30",
    rental_income_weekly: "",
    is_owner_occupied: false,
    land_value: "",
    notes: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.streetAddress,
      suburb: addressData.suburb,
      state: addressData.state,
      postcode: addressData.postcode,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const required = ["address", "suburb", "state", "postcode", "property_type", 
      "purchase_price", "purchase_date", "current_value", "loan_amount", 
      "interest_rate", "rental_income_weekly"];
    
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in the ${field.replace(/_/g, " ")} field`);
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price),
        current_value: parseFloat(formData.current_value),
        loan_amount: parseFloat(formData.loan_amount),
        interest_rate: parseFloat(formData.interest_rate),
        loan_term_years: parseInt(formData.loan_term_years),
        rental_income_weekly: parseFloat(formData.rental_income_weekly),
        land_value: parseFloat(formData.land_value) || 0,
      };

      const response = await createProperty(payload);
      toast.success("Property added successfully!");
      // Navigate to properties list to ensure data is synced
      navigate("/properties");
    } catch (error) {
      console.error("Error creating property:", error);
      toast.error("Failed to add property. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="add-property-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/properties">
          <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Add New Property
          </h2>
          <p className="text-slate-500">
            Enter your property details to start tracking
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Property Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#547d91]" />
              Property Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Street Address *</Label>
              <AddressAutocomplete
                id="address"
                placeholder="Start typing an address..."
                value={formData.address}
                onChange={(value) => handleChange("address", value)}
                onAddressSelect={handleAddressSelect}
                data-testid="address-input"
              />
              <p className="text-xs text-slate-500">Start typing to auto-populate address fields</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="suburb">Suburb *</Label>
                <Input
                  id="suburb"
                  placeholder="Sydney"
                  value={formData.suburb}
                  onChange={(e) => handleChange("suburb", e.target.value)}
                  data-testid="suburb-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select 
                  value={formData.state} 
                  onValueChange={(value) => handleChange("state", value)}
                >
                  <SelectTrigger data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((state) => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postcode *</Label>
                <Input
                  id="postcode"
                  placeholder="2000"
                  value={formData.postcode}
                  onChange={(e) => handleChange("postcode", e.target.value)}
                  data-testid="postcode-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type *</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(value) => handleChange("property_type", value)}
                >
                  <SelectTrigger data-testid="property-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="purchase_date">Purchase Date *</Label>
                <Input
                  id="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => handleChange("purchase_date", e.target.value)}
                  data-testid="purchase-date-input"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="is_owner_occupied" className="font-medium">Owner Occupied</Label>
                <p className="text-sm text-slate-500">Is this your primary residence?</p>
              </div>
              <Switch
                id="is_owner_occupied"
                checked={formData.is_owner_occupied}
                onCheckedChange={(checked) => handleChange("is_owner_occupied", checked)}
                data-testid="owner-occupied-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Financial Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Financial Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Purchase Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="purchase_price"
                    type="number"
                    className="pl-7"
                    placeholder="750000"
                    value={formData.purchase_price}
                    onChange={(e) => handleChange("purchase_price", e.target.value)}
                    data-testid="purchase-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="current_value">Current Value *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="current_value"
                    type="number"
                    className="pl-7"
                    placeholder="850000"
                    value={formData.current_value}
                    onChange={(e) => handleChange("current_value", e.target.value)}
                    data-testid="current-value-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="loan_amount">Loan Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <Input
                    id="loan_amount"
                    type="number"
                    className="pl-7"
                    placeholder="600000"
                    value={formData.loan_amount}
                    onChange={(e) => handleChange("loan_amount", e.target.value)}
                    data-testid="loan-amount-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="interest_rate">Interest Rate (%) *</Label>
                <Input
                  id="interest_rate"
                  type="number"
                  step="0.01"
                  placeholder="6.5"
                  value={formData.interest_rate}
                  onChange={(e) => handleChange("interest_rate", e.target.value)}
                  data-testid="interest-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="loan_term_years">Loan Term (years)</Label>
                <Input
                  id="loan_term_years"
                  type="number"
                  placeholder="30"
                  value={formData.loan_term_years}
                  onChange={(e) => handleChange("loan_term_years", e.target.value)}
                  data-testid="loan-term-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rental_income_weekly">Weekly Rental Income *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input
                  id="rental_income_weekly"
                  type="number"
                  className="pl-7"
                  placeholder="650"
                  value={formData.rental_income_weekly}
                  onChange={(e) => handleChange("rental_income_weekly", e.target.value)}
                  data-testid="rental-income-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                Enter 0 if owner-occupied or vacant
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Land Value */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Land Value</CardTitle>
            <p className="text-sm text-slate-500">
              Optional: Enter the land component of your property value.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="land_value">Land Value</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <Input
                  id="land_value"
                  type="number"
                  className="pl-7"
                  placeholder="300000"
                  value={formData.land_value}
                  onChange={(e) => handleChange("land_value", e.target.value)}
                  data-testid="land-value-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Add any notes about this property..."
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              rows={3}
              data-testid="notes-input"
            />
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <Link to="/properties">
            <Button type="button" variant="outline" className="rounded-full px-8">
              Cancel
            </Button>
          </Link>
          <Button 
            type="submit" 
            className="bg-[#547d91] hover:bg-[#456a7a] rounded-full px-8"
            disabled={isSubmitting}
            data-testid="submit-property-btn"
          >
            {isSubmitting ? "Adding..." : "Add Property"}
          </Button>
        </div>
      </form>
    </div>
  );
}
